import React, { useState, useMemo } from "react";
import {
  Form,
  Select,
  Input,
  Button,
  Table,
  Popconfirm,
  Space,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";

type Props = {
  categories: any[];
  subcategories: any[];
  loadingSubs: boolean;
  createSub: any;
  updateSub: any;
  deleteSub: any;
};

export const ExpenseSubCategory: React.FC<Props> = ({
  categories,
  subcategories,
  loadingSubs,
  createSub,
  updateSub,
  deleteSub,
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );

  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c: any) => {
      map[c.id] = c.name;
    });
    return map;
  }, [categories]);

  const parentOptions = useMemo(() => {
    return categories.map((c: any) => ({
      value: c.id,
      label: c.name,
    }));
  }, [categories]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      createSub.mutate(
        {
          category_id: values.category_id,
          name: values.name.trim(),
        },
        {
          onSuccess: (res: any) => {
            if (res.success) {
              message.success("Subcategory created successfully!");
              form.resetFields();
            } else {
              message.error(res.message || "Failed to create subcategory.");
            }
          },
          onError: () => message.error("Something went wrong."),
        },
      );
    } catch {
      message.error("Please fill all required fields.");
    }
  };

  const handleSaveEdit = async (
    record: any,
    newName: string,
    categoryId: string,
  ) => {
    if (!newName.trim()) {
      message.error("Subcategory name cannot be empty.");
      return;
    }
    updateSub.mutate(
      {
        id: record.id,
        category_id: categoryId,
        name: newName.trim(),
      },
      {
        onSuccess: (res: any) => {
          if (res.success) {
            message.success("Subcategory updated successfully!");
            setEditingId(null);
            setEditingCategoryId(null);
          } else {
            message.error(res.message || "Failed to update subcategory.");
          }
        },
        onError: () => message.error("Something went wrong."),
      },
    );
  };

  const handleDelete = async (id: string) => {
    deleteSub.mutate(id, {
      onSuccess: (res: any) => {
        if (res.success) {
          message.success("Subcategory deleted successfully!");
        } else {
          message.error(res.message || "Failed to delete subcategory.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Subcategory Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Input
            defaultValue={text}
            id={`sub-input-${record.id}`}
            size="small"
            onPressEnter={(e: any) => {
              if (editingCategoryId) {
                handleSaveEdit(record, e.target.value, editingCategoryId);
              }
            }}
            style={{ width: "100%" }}
          />
        ) : (
          <span className="font-medium text-gray-700">{text}</span>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category_id",
      key: "category_id",
      render: (catId: string, record: any) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Select
            defaultValue={catId}
            options={parentOptions}
            size="small"
            onChange={(val) => setEditingCategoryId(val)}
            style={{ width: "100%" }}
          />
        ) : (
          <span className="text-gray-500">{categoryMap[catId] || "—"}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: any) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Space size="middle">
            <Button
              type="text"
              icon={<SaveOutlined className="text-green-600" />}
              size="small"
              onClick={() => {
                const input = document.getElementById(
                  `sub-input-${record.id}`,
                ) as HTMLInputElement;
                if (input && editingCategoryId) {
                  handleSaveEdit(record, input.value, editingCategoryId);
                }
              }}
            />
            <Button
              type="text"
              icon={<CloseOutlined className="text-gray-400" />}
              size="small"
              onClick={() => {
                setEditingId(null);
                setEditingCategoryId(null);
              }}
            />
          </Space>
        ) : (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditingId(record.id);
                setEditingCategoryId(record.category_id);
              }}
            />
            <Popconfirm
              title="Are you sure you want to delete this subcategory?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Form
        form={form}
        layout="inline"
        className="my-4 flex items-center justify-between"
      >
        <Form.Item
          name="category_id"
          rules={[{ required: true, message: "Category is required" }]}
          style={{ width: 180, marginRight: 8 }}
        >
          <Select placeholder="Select Category" options={parentOptions} />
        </Form.Item>

        <Form.Item
          name="name"
          rules={[{ required: true, message: "Subcategory name is required" }]}
          style={{ flex: 1, marginRight: 8 }}
        >
          <Input
            placeholder="e.g. Electricity Bill, Office Supplies"
            prefix={<PlusOutlined />}
          />
        </Form.Item>

        <Form.Item style={{ marginRight: 0 }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleCreate}
            loading={createSub.isPending}
          >
            Add Subcategory
          </Button>
        </Form.Item>
      </Form>

      <Table
        dataSource={subcategories}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loadingSubs}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </>
  );
};
